import React, { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { MapPin } from "lucide-react";
import { requireAuth, useAuth } from "../../providers/auth";
import Meta from "../../components/custom-ui/meta/Meta";
import {
  useCommunityHubById,
  useHubModerators,
  useHubPosts,
  userCanModerateHub,
} from "../../data/communityHub";
import CommunityHubManageSidebar from "../../components/community/admin/CommunityHubManageSidebar";
import CommunityHubLogo from "../../components/community/shared/CommunityHubLogo";
import CommunityPostComposer from "../../components/community/feed/CommunityPostComposer";
import CommunityFeedPost from "../../components/community/feed/CommunityFeedPost";
import ImageLightbox from "../../components/EventManagement/Component/ImageLightBox";
import ToastAlert from "../../components/custom-ui/toast-alert/ToastAlert";

function AdminCommunityDetailsPage() {
  const { communityId } = useParams<{ communityId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user }: any = useAuth();
  const hubId = communityId || "";

  const { data: hubFromDb, isLoading } = useCommunityHubById(hubId);
  const community = hubFromDb || location.state?.community;
  const { data: extraModerators = [] } = useHubModerators(hubId);
  const { data: posts = [] } = useHubPosts(hubId);

  const canModerate = userCanModerateHub(
    community,
    user?.email,
    user?.claims?.role,
    extraModerators.map((m: any) => m.email),
  );

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxData, setLightboxData] = useState(null as {
    images: string[];
    title: string;
    description: string;
    author: string;
    post_type: string;
  } | null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openPostLightbox = (
    post: { title?: string; body?: string; author?: { name?: string } },
    images: string[],
    index: number,
  ) => {
    setLightboxData({
      images,
      title: post.title || "",
      description: post.body || "",
      author: post.author?.name || "",
      post_type: "post",
    });
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  if (!community) {
    if (isLoading) return null;
    return (
      <div className="pt-44 px-6 text-center text-gray-500">
        Community not found.
        <button
          type="button"
          className="block mx-auto mt-4 text-[#1E596D] font-semibold"
          onClick={() => navigate("/admin-community")}
        >
          ← Back to Communities
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F7F8] pt-44 pb-8">
      <Meta title="Community" />

      <div className="max-w-[1400px] mx-auto px-6 mb-4">
        <nav className="flex items-center gap-2 text-sm">
          <button
            type="button"
            onClick={() => navigate("/admin-community")}
            className="text-gray-500 hover:text-[#15415E] font-medium transition-colors"
          >
            ← Back to Communities
          </button>
          <span className="text-gray-400">/</span>
          <span className="text-[#15415E] font-semibold">{community.hubName}</span>
        </nav>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 mb-6">
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-center gap-4">
            <CommunityHubLogo hubName={community.hubName} size={56} />
            <div>
              <h1 className="text-2xl font-extrabold text-[#15415E] leading-tight">
                {community.hubName}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-gray-500">
                <span className="flex items-center gap-1 font-medium">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="text-gray-400"
                  >
                    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                  </svg>
                  <b className="text-[#15415E]">
                    {community.hubMembers ?? community.memberCount ?? 0}
                  </b>{" "}
                  members
                </span>
                {community.location && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span className="flex items-center gap-1">
                      <MapPin size={13} className="text-gray-400" />
                      {community.location}
                    </span>
                  </>
                )}
                <span className="text-gray-300">|</span>
                <span>
                  Moderated by{" "}
                  <b className="text-[#15415E]">
                    {community.moderatorBy?.name || "—"}
                  </b>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 pb-20">
        {canModerate ? (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-6 md:gap-8 items-start">
            <div className="md:col-span-2 lg:col-span-2 xl:col-span-2 2xl:col-span-2 min-w-0 space-y-5">
              <CommunityPostComposer hubId={community.id} />
              {posts.map((post: any) => (
                <CommunityFeedPost
                  key={post.id}
                  hubId={community.id}
                  post={post}
                  canModerate={canModerate}
                  canComment={canModerate}
                  onOpenLightbox={(images: string[], index: number) =>
                    openPostLightbox(post, images, index)
                  }
                />
              ))}
              {posts.length === 0 && (
                <p className="text-center text-gray-400 py-12 bg-white rounded-2xl border border-gray-100 shadow-sm">
                  No posts yet. Create the first post above.
                </p>
              )}
            </div>

            <div className="md:col-span-1 lg:col-span-1 xl:col-span-1 2xl:col-span-1 min-w-0 w-full md:max-w-[320px] md:justify-self-end">
              <CommunityHubManageSidebar hub={community} canManage={canModerate} />
            </div>
          </div>
        ) : (
          <div className="max-w-[900px] mx-auto space-y-5">
            {posts.map((post: any) => (
              <CommunityFeedPost
                key={post.id}
                hubId={community.id}
                post={post}
                canModerate={canModerate}
                canComment={canModerate}
                onOpenLightbox={(images: string[], index: number) =>
                  openPostLightbox(post, images, index)
                }
              />
            ))}
            {posts.length === 0 && (
              <p className="text-center text-gray-400 py-12 bg-white rounded-2xl border border-gray-100 shadow-sm">
                No posts yet.
              </p>
            )}
          </div>
        )}
      </div>

      <ImageLightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        data={lightboxData}
        initialIndex={lightboxIndex}
      />

      <ToastAlert containerStyle={{ zIndex: 10000 }} />
    </div>
  );
}

export default requireAuth(AdminCommunityDetailsPage);
